import { useState } from "react";
import type { MediaItem } from "~/features/mediaLibrary/types/mediaLibraryTypes";
import Tag from "~/common/components/Tag/Tag";
import { Box, IconButton, Stack, Typography, styled, type SxProps, type Theme, useTheme } from "~/common/mui";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import FavoriteOutlinedIcon from "@mui/icons-material/FavoriteOutlined";
import PlayCircleFilledWhiteOutlinedIcon from "@mui/icons-material/PlayCircleFilledWhiteOutlined";
import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";
import FolderZipOutlinedIcon from "@mui/icons-material/FolderZipOutlined";
import { MEDIA_TYPES, MediaType } from "~/features/mediaLibrary/constants";
import { lineClamp } from "~/common/components/icon/FontUtils";
import { useTranslation } from "react-i18next";

const centeredIconSx: SxProps<Theme> = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
};

const unknownFileIconSx: SxProps<Theme> = {
  color: "text.label",
  width: "128px",
  height: "128px",
};

const imageSx: SxProps<Theme> = {
  width: 1,
  height: 1,
  objectFit: "contain",
};

export default function MediaLibraryItem(props: {
  media: MediaItem;
  isDownloading: boolean;
  handleCardClick: (media: MediaItem) => void;
  handleDownload: (media: MediaItem) => void;
  handleFavouriteClick: (
    id: string,
    updatedFav: boolean,
    title?: string,
    language?: string,
    mediaType?: string,
  ) => void;
}) {
  const { t } = useTranslation();
  const { media, handleCardClick, handleFavouriteClick, handleDownload, isDownloading } = props;
  const { id, previewUrl, mediaType, isFavourite, title, language, documentType } = media;
  const theme = useTheme();
  const handleFavourite = async () => {
    await handleFavouriteClick(id, !isFavourite, title, language, mediaType);
  };

  const mediaActionOverLay = () => (
    <Box sx={{ width: 1 }}>
      {language && (
        <Tag
          className="media-library-lang-tag"
          style={{
            position: "absolute",
            left: theme.spacing(2),
            top: theme.spacing(2),
          }}
        >
          {language}
        </Tag>
      )}
      {media?.isLatestVersion && (
        <Tag
          variant="success"
          style={{
            position: "absolute",
            left: "57px",
            top: "16px",
          }}
        >
          {t("sds.latest.version")}
        </Tag>
      )}
      <Box
        sx={{
          position: "absolute",
          right: "8px",
          top: "8px",
          display: "flex",
          gap: 1,
          opacity: 0,
          visibility: "hidden",
          transition: "opacity 0.3s ease, visibility 0.3s ease",
          [theme.breakpoints.down("desktop-s")]: {
            opacity: 1,
            visibility: "visible",
          },
        }}
        className="media-library-item-actions"
      >
        <IconButton
          variant="contained"
          size={"medium"}
          color={"secondary"}
          disabled={isDownloading}
          onClick={async (e) => {
            e.stopPropagation();
            e.preventDefault();
            handleDownload(media);
          }}
        >
          <FileDownloadOutlinedIcon />
        </IconButton>
        <IconButton
          variant="contained"
          size={"medium"}
          color={"secondary"}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleFavourite();
          }}
        >
          {isFavourite ? <FavoriteOutlinedIcon /> : <FavoriteBorderOutlinedIcon />}
        </IconButton>
      </Box>
    </Box>
  );

  const mediaPlayIcon = () => {
    return (
      MEDIA_TYPES[mediaType] == MediaType.VIDEO && (
        <IconButton sx={centeredIconSx} variant="contained" size={"medium"} color={"secondary"} onClick={() => {}}>
          <PlayCircleFilledWhiteOutlinedIcon />
        </IconButton>
      )
    );
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          cursor: "pointer",
          width: 1,
          minWidth: 0,
        }}
        key={id}
        onClick={() => {
          handleCardClick(media);
        }}
      >
        <Box
          sx={{
            width: 1,
            minWidth: 0,
            height: "354px",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
            bgcolor: "surface.bright",
            position: "relative",
            "&:hover .media-library-item-actions": {
              opacity: 1,
              visibility: "visible",
            },
          }}
        >
          {mediaActionOverLay()}
          {mediaPlayIcon()}
          <ImageWithFallback previewUrl={previewUrl} mediaType={mediaType} previewImageSx={imageSx} />
        </Box>
        <Stack gap={0.5}>
          <TruncatedTitle variant="titleMedium">{title}</TruncatedTitle>
          <TruncatedSubTitle>{documentType}</TruncatedSubTitle>
        </Stack>
      </Box>
    </>
  );
}

export const ImageWithFallback = ({
  previewUrl,
  mediaType,
  previewImageSx,
}: {
  previewUrl: string;
  mediaType: string;
  previewImageSx?: SxProps<Theme>;
}) => {
  const [loading, setLoading] = useState(true);
  const [imgError, setImgError] = useState(false);

  if (!previewUrl) {
    return fallbackImage();
  }

  if (MEDIA_TYPES[mediaType] === MediaType.ZIP) {
    return (
      <Box sx={centeredIconSx}>
        <FolderZipOutlinedIcon sx={unknownFileIconSx} />
      </Box>
    );
  }

  return (
    <>
      {imgError && !loading ? (
        fallbackImage()
      ) : (
        <Box
          component="img"
          src={previewUrl}
          alt={mediaType}
          sx={previewImageSx}
          onLoad={() => setLoading(false)}
          onError={() => {
            setLoading(false);
            setImgError(true);
          }}
        />
      )}
    </>
  );
};

const fallbackImage = () => {
  return (
    <Box sx={centeredIconSx}>
      <BrokenImageOutlinedIcon sx={unknownFileIconSx} />
    </Box>
  );
};

const TruncatedTitle = styled(Typography)(({ theme }) => ({
  ...lineClamp(2),
  color: theme.palette.text.primary,
}));
const TruncatedSubTitle = styled(Typography)(({ theme }) => ({
  ...lineClamp(1),
  color: theme.palette.text.secondary,
}));
